import {
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CollapseAllButton,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  EXTRAS_MENU_TOOLTIP_PROPS,
  ExpandAllButton,
  ExportMenuItems,
  FormControlSizes,
  HStack,
  HelpIcon,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Text,
  Toggle,
  columnTypes,
  useAccordionFilterBuilder,
  useAggDeltaUpdatesPipe,
  useBlotterGroupingToggling,
  useBlotterTable,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMixpanel,
  usePersistedBlotterTable,
  useSyncedRef,
  useWSFilterPipe,
  type Leaves,
} from '@talos/kyoko';
import { useMemo } from 'react';

import { CONTROL_TOOLTIPS } from '@src/containers/Blotters/tooltips';
import { useFeatureFlag } from '@src/hooks';
import type { StateUpdatedEvent } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useGetBulkClosablePositions } from '../BulkClosePosition/useGetBulkClosablePositions';
import { defaultUnifiedBlotterColumns, defaultUnifiedBlotterFilter } from '../defaults';
import type { ClonePositionsTabFunc, UnifiedPositionsTabProps } from '../types';
import { usePositionsMenu } from '../usePositionsMenu';
import { useShowAdjustMarketAccountPositionButton } from '../useShowAdjustMarketAccountPositionButton';
import type { UnifiedPosition } from './UnifiedPosition';
import { POSITIONS_UNIFIED_GROUPINGS } from './positionsUnifiedGroupings';
import { usePositionsUnifiedColumns } from './usePositionsUnifiedColumns';
import { usePositionsUnifiedFilter } from './usePositionsUnifiedFilter';
import { useUnifiedPositionsBalancesObs } from './useUnifiedPositionsBalancesObs';
import { useUnifiedPositionsClientFilter } from './useUnifiedPositionsClientFilter';

export interface PositionsUnifiedBlotterParams {
  blotterID: string;
  initialIsOpen?: boolean;
  tab: UnifiedPositionsTabProps;
  onUpdateTab: (tab: UnifiedPositionsTabProps) => void;
  onCloneTab: ClonePositionsTabFunc;
  tag?: string;
}

function getPositionKey(position: UnifiedPosition) {
  return position.rowID;
}

const aggSpecs = [
  { valuePath: 'Equivalent.Delta', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'Equivalent.UnrealizedPnL', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'Equivalent.Amount', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'talosEquityEquivalent', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'balanceEquivalent', currencyPath: 'Equivalent.Currency' },
] satisfies { valuePath: Leaves<UnifiedPosition>; currencyPath: Leaves<UnifiedPosition> }[];

/**
 * Positions blotter for the Market Accounts tab.
 *
 * Internally this stays "Unified" because that was the UI label for a long time.
 * Renaming the component (e.g. PositionsMarketAccountsBlotter) would be more
 * confusing than keeping the established code name.
 */
export function PositionsUnifiedBlotter({
  blotterID,
  tab,
  onUpdateTab,
  initialIsOpen,
  onCloneTab,
  tag,
}: PositionsUnifiedBlotterParams) {
  const { enableBulkClosePosition, enableLegacyUpdateBalances } = useFeatureFlag();
  const mixpanel = useMixpanel();

  const defaultBlotterColumns = tab.defaultColumns ?? defaultUnifiedBlotterColumns;
  const defaultFilter = tab.defaultFilter ?? defaultUnifiedBlotterFilter;
  const showZeroBalances = tab.showZeroBalances ?? false;
  const includeCash = tab.includeCash ?? true;

  const defaultColumns = usePositionsUnifiedColumns(defaultBlotterColumns);

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter });

  const filterResults = usePositionsUnifiedFilter({
    persistedBlotterTable,
    defaultFilter,
  });

  const { filterBuilderProps } = filterResults;

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    onGroupingStateUpdated(event);
  });

  const updateShowZeroBalances = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowZeroBalances);
    onUpdateTab({ ...tab, showZeroBalances: value });
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const positionsMenu = usePositionsMenu<UnifiedPosition>({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
    enableBulkClose: true,
    tabType: 'Unified',
    showOpenTransactionsMenuItem: true,
  });

  const columnsWithMenu = useMemo(
    () => [...defaultColumns, ...positionsMenu.columns],
    [defaultColumns, positionsMenu.columns]
  );

  const aggPipe = useAggDeltaUpdatesPipe({
    getUniqueKey: getPositionKey,
    aggSpecs,
  });

  const rxjsFilterFunc = useUnifiedPositionsClientFilter({
    filter: filterResults.filter,
    includeCash,
  });

  const filterPipe = useWSFilterPipe({ getUniqueKey: getPositionKey, filterFunc: rxjsFilterFunc });

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        title: 'Grouping',
        editable: false,
        width: 300,
        hide: false,
        sortable: true,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const { dataObservable, errorObservable } = useUnifiedPositionsBalancesObs({
    tag: blotterID,
    showZeroBalances,
    filter: filterResults.filter,
  });

  const filteredDataObservable = useMemo(() => dataObservable.pipe(filterPipe), [dataObservable, filterPipe]);

  const blotterTable = useBlotterTable<UnifiedPosition>({
    dataObservable: filteredDataObservable,
    errorObservable,
    pinnedRowDataPipe: aggPipe,
    rowID: 'rowID' satisfies keyof UnifiedPosition,
    persistence: persistedBlotterTable,
    columns: columnsWithMenu,
    getExtraMainMenuItems: positionsMenu.getExtraMainMenuItems,
    contextMenuOptions: positionsMenu.contextMenuOptions,
    gridOptions: {
      onStateUpdated: handleGridStateUpdated,
      groupHideParentOfSingleChild: 'leafGroupsOnly',
      autoGroupColumnDef,
      rowGroupPanelShow: 'always',
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      groupDisplayType: 'singleColumn',
    },
    exportOptions: {
      name: 'PositionsUnified',
      tabLabel: tab.label,
    },
  });

  const { expandAllGroups, collapseAllGroups, setRowGroupColumns } = blotterTable;

  const { onStateUpdated: onGroupingStateUpdated, groupingToggleButtons } = useBlotterGroupingToggling({
    blotterName: 'positions unified',
    setRowGroupColumns,
    groupings: POSITIONS_UNIFIED_GROUPINGS,
    allowNoGrouping: true,
  });

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab?.();
  });

  const { getBulkClosablePositions } = useGetBulkClosablePositions();
  // We make this function stable so we don't do too much work and risk changing the suggested SendOrderArgs returned mid-confirmation.
  const stableGetBulkClosablePositions = useSyncedRef(getBulkClosablePositions);

  const handleCloseAll = useCallbackRef(() => {
    // Important that we get the rows after the aggrid client side filter has been applied.
    // Meaning that we get the rows actually visible to the user.
    const rows = blotterTable.getRowsAfterFilter();
    const positions = compact(rows.map(row => row.data));
    const orderArgsForClosing = stableGetBulkClosablePositions.current(positions);
    positionsMenu.openBulkClosePositionDialog(orderArgsForClosing);
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();
  const showAdjustMarketAccountPositionButton = useShowAdjustMarketAccountPositionButton();

  const updateIncludeCash = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleIncludeCash, {
      [MixpanelEventProperty.Enabled]: value,
      [MixpanelEventProperty.Source]: MixpanelEventSource.UnifiedPositionsBlotter,
    });
    onUpdateTab({ ...tab, includeCash: value });
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        accordionBodyPrefix={groupingToggleButtons}
        portalId={BLOTTER_TABLE_FILTERS_CONTAINER_ID}
        prefix={
          <>
            <ExpandAllButton onClick={expandAllGroups} data-testid="expand-all-button" />
            <CollapseAllButton onClick={collapseAllGroups} data-testid="collapse-all-button" />
          </>
        }
        suffix={
          <>
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              <Toggle
                tooltip={CONTROL_TOOLTIPS.showZeroBalances}
                tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                size={FormControlSizes.Small}
                checked={showZeroBalances}
                onChange={updateShowZeroBalances}
                label="Show Zero Balances"
              />
              <Toggle
                tooltip={CONTROL_TOOLTIPS.includeCash}
                tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                size={FormControlSizes.Small}
                checked={includeCash}
                onChange={updateIncludeCash}
                label="Include Cash"
              />
              <Divider orientation="horizontal" />
              {enableBulkClosePosition && (
                <Button
                  startIcon={IconName.Close}
                  size={FormControlSizes.Small}
                  onClick={handleCloseAll}
                  data-testid="bulk-close-all-button"
                >
                  <HStack gap="spacing4">
                    <Text>Close All</Text>
                    <HelpIcon tooltip="Close All will bring up the Bulk Close Positions preview dialog for all positions currently present in the blotter." />
                  </HStack>
                </Button>
              )}
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>

              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />

              {showAdjustMarketAccountPositionButton && (
                <Button
                  startIcon={IconName.Pencil}
                  variant={ButtonVariants.Default}
                  size={FormControlSizes.Small}
                  data-testid="update-balances-button"
                  onClick={() => {
                    positionsMenu.openUpdateBalancesDialog();
                  }}
                >
                  {enableLegacyUpdateBalances ? 'Update Balances' : 'Adjust Market Account Position'}
                </Button>
              )}
            </BlotterTableExtrasMenu>
          </>
        }
      />
      <BlotterTable {...blotterTable} />
      {positionsMenu.dialogs}
    </>
  );
}
